import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { useDidUpdate } from 'rooks';
import { chain } from 'lodash';
import moment from 'moment';
import { Button, Spin } from 'antd';
import {
  Pie,
  Sector,
  Legend,
  Tooltip,
  Surface,
  Symbols,
  PieChart,
  ResponsiveContainer,
} from 'recharts';

import ChartDateRangePicker, {
  DateRangeFilterType,
} from '../ChartDateRangePicker';
import {
  getDateWithoutTime,
  getDateRangeTillLastSunday,
} from 'utils/dateUtils';
import {
  SortingEnum,
  SummaryTrendByCustomerReportViewModel,
} from 'api/backofficeApiClient';
import { DateParams } from 'utils/filterUtils';
import { changeOtherChartItemIndex, dateRangeDurationInWeeks } from '../utils';
import { getCustomerTrendChartData } from 'redux/reducers/homeDashboardSlice';
import { CustomerBillingProfitSortFieldEnum } from 'features/home/UserHomePage/enums';
import { useAppDispatch } from 'hooks/useAppDispatch';
import cx from 'classnames';

import styles from '../../UserHomePage.module.less';

interface ChartColorSettings {
  id: string;
  fill: string;
  fillColor: string;
  gradientStartColor: string;
  gradientEndColor: string;
}

interface CustomerMixBillingChartItem
  extends ChartColorSettings,
    SummaryTrendByCustomerReportViewModel {}

const chartPageSize = 5;
const animationDuration = 1500;
const pieDefaultProps = {
  dataKey: 'value',
  animationDuration,
  width: 192,
  height: 192,
  innerRadius: 50,
  outerRadius: 110,
};

const chartColorSettings: ChartColorSettings[] = [
  {
    id: 'gradCustomerMixBillingFirst',
    fill: 'url(#gradCustomerMixBillingFirst)',
    fillColor: '#9CEBF3',
    gradientStartColor: '#BFFFDE',
    gradientEndColor: '#85DEFF',
  },
  {
    id: 'gradCustomerMixBillingSecond',
    fill: 'url(#gradCustomerMixBillingSecond)',
    fillColor: '#FFDC98',
    gradientStartColor: '#FFD78A',
    gradientEndColor: '#FFE7BA',
  },
  {
    id: 'gradCustomerMixBillingThird',
    fill: 'url(#gradCustomerMixBillingThird)',
    fillColor: '#81C785',
    gradientStartColor: '#65BB6A',
    gradientEndColor: '#A5D6A7',
  },
  {
    id: 'gradCustomerMixBillingFourth',
    fill: 'url(#gradCustomerMixBillingFourth)',
    fillColor: '#B28DF4',
    gradientStartColor: '#9B70E7',
    gradientEndColor: '#C6A6FF',
  },
  {
    id: 'gradCustomerMixBillingOther',
    fill: 'url(#gradCustomerMixBillingOther)',
    fillColor: '#FF8981',
    gradientStartColor: '#FF8178',
    gradientEndColor: '#FFA8A1',
  },
];

const getFormattedProportionData = (
  proportionData: CustomerMixBillingChartItem,
) =>
  `${proportionData.customerName}: ${(
    proportionData.percentageOfGreatTotal as number
  ).toFixed(2)}%`;

const renderTooltip = (props: any) => {
  if (props.active) {
    const chartItem: CustomerMixBillingChartItem = props.payload[0].payload;

    return (
      <div className={styles.tooltip}>
        <span
          className={styles.colorBox}
          style={{
            backgroundColor: chartItem.fillColor,
          }}
        />
        <span>{getFormattedProportionData(chartItem)}</span>
      </div>
    );
  }

  return null;
};

const renderActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
    props;

  return (
    <g id={props.chartId}>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius - 3}
        outerRadius={outerRadius + 3}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        filter="url(#customerMixBillingChartShadow)"
      />
    </g>
  );
};

const renderLegend = (props: any) => {
  const payload: CustomerMixBillingChartItem[] = props.payload;

  return (
    <ul className={styles.legend}>
      {payload.map((entry, index) => (
        <li className={styles.legendItem} key={`legend-item-${index}`}>
          <Surface width={12} height={12}>
            <Symbols
              cx={6}
              cy={6}
              size={100}
              type="circle"
              fill={entry.fillColor}
            />
          </Surface>

          <span className={styles.legendName}>
            {getFormattedProportionData(entry)}
          </span>
        </li>
      ))}
    </ul>
  );
};

const getFormattedChartData = (
  data: SummaryTrendByCustomerReportViewModel[],
): CustomerMixBillingChartItem[] => {
  const sortedChartData = chain(data)
    .filter((item) => !!item.percentageOfGreatTotal)
    .orderBy((item) => item.percentageOfGreatTotal, 'desc')
    .value();

  const updatedIndexChartData = changeOtherChartItemIndex(
    sortedChartData,
    'customerName',
  );

  return updatedIndexChartData.map((chartItem, index) => ({
    ...chartItem,
    ...chartColorSettings[index],
    value: chartItem.percentageOfGreatTotal,
  }));
};

export default function CustomerTrendChart() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const {
    mixBillingStart: startDate,
    mixBillingEnd: endDate,
    ...restQueryParams
  } = queryString.parse(location.search);
  const defaultDateFilter: DateParams = getDateRangeTillLastSunday(
    dateRangeDurationInWeeks,
  );
  const initialDateFilter: DateRangeFilterType =
    startDate && endDate
      ? {
          startDate: moment(startDate, 'MM/DD/YYYY'),
          endDate: moment(endDate, 'MM/DD/YYYY'),
        }
      : defaultDateFilter;
  const [dateFilter, setDateFilter] =
    useState<DateRangeFilterType>(initialDateFilter);
  const [isInitialAnimationActive, setIsInitialAnimationActive] =
    useState<boolean>(true);
  const [chartData, setChartData] = useState<
    SummaryTrendByCustomerReportViewModel[]
  >([]);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>();
  const chartId = useRef(`chart-id-${Date.now()}`);
  const formattedChartData = useMemo(
    () => getFormattedChartData(chartData),
    [chartData],
  );
  const isDisabled = chartData.every(
    (chartItem) => !chartItem.percentageOfGreatTotal,
  );

  useEffect(() => {
    setTimeout(() => {
      setIsInitialAnimationActive(false);
    }, animationDuration + 100);
  }, []);

  useDidUpdate(() => {
    const queryData = {
      mixBillingStart: dateFilter?.startDate
        ? dateFilter.startDate.format('MM/DD/YYYY')
        : undefined,
      mixBillingEnd: dateFilter?.endDate
        ? dateFilter.endDate.format('MM/DD/YYYY')
        : undefined,
      ...restQueryParams,
    };

    navigate(`${location.pathname}?${queryString.stringify(queryData)}`);
    // eslint-disable-next-line
  }, [dateFilter]);

  const loadCustomerTrendChartData = useCallback(async () => {
    setIsChartLoading(true);

    const result = await dispatch(
      getCustomerTrendChartData({
        pageSize: chartPageSize,
        sortField: CustomerBillingProfitSortFieldEnum.TotalPercentage,
        sortType: SortingEnum.Desc,
        startDate: dateFilter?.startDate
          ? getDateWithoutTime(dateFilter.startDate)
          : undefined,
        endDate: dateFilter?.endDate
          ? getDateWithoutTime(dateFilter.endDate)
          : undefined,
      }),
    );

    if (getCustomerTrendChartData.fulfilled.match(result)) {
      setChartData(result.payload || []);
    } else {
      setChartData([]);
    }

    setIsChartLoading(false);
  }, [dispatch, dateFilter]);

  useEffect(() => {
    loadCustomerTrendChartData();
  }, [loadCustomerTrendChartData]);

  const onPieEnter = (data: any, index: number) => {
    setActiveIndex(index);
  };

  return (
    <div className={styles.chartWrapper}>
      <div className={styles.selectBox}>
        <ChartDateRangePicker
          pickerKey="customerMixBillingFilterByDate"
          value={dateFilter}
          onChange={setDateFilter}
        />
      </div>

      <div className={styles.title}>
        <Link to="/customer-trends-mix-report-details">
          Customer Mix - Billing
        </Link>
        <Button
          type="primary"
          onClick={() => navigate('/customer-trends-mix-report-details')}
        >
          View Details
        </Button>
      </div>

      <Spin spinning={isChartLoading}>
        <div
          className={cx(styles.pieChartWrapper, styles.pieChartHeight)}
          style={{
            pointerEvents: isInitialAnimationActive ? 'none' : 'auto',
          }}
        >
          <ResponsiveContainer width="100%">
            <PieChart>
              <defs>
                {chartColorSettings.map((chartSet) => (
                  <radialGradient
                    id={chartSet.id}
                    key={chartSet.id}
                    gradientUnits="userSpaceOnUse"
                    cx="25%"
                    r="100%"
                  >
                    <stop offset="0%" stopColor={chartSet.gradientStartColor} />
                    <stop offset="50%" stopColor={chartSet.gradientEndColor} />
                  </radialGradient>
                ))}

                <filter
                  id="customerMixBillingChartShadow"
                  x="0"
                  y="0"
                  width="150%"
                  height="150%"
                  filterUnits="userSpaceOnUse"
                >
                  <feDropShadow
                    dx="0"
                    dy="0"
                    stdDeviation="3"
                    floodColor="#4a6778"
                  />
                </filter>
              </defs>

              {isDisabled && !isChartLoading && (
                <Pie
                  data={[
                    {
                      value: 1,
                      fill: '#D8D8D8',
                    },
                  ]}
                  {...pieDefaultProps}
                />
              )}

              {!isDisabled && (
                <Pie
                  activeIndex={activeIndex}
                  activeShape={(props) =>
                    renderActiveShape({ ...props, chartId: chartId.current })
                  }
                  data={formattedChartData}
                  onMouseEnter={onPieEnter}
                  onMouseLeave={() => setActiveIndex(undefined)}
                  {...pieDefaultProps}
                />
              )}

              <Legend
                payload={formattedChartData as any}
                align="left"
                verticalAlign="top"
                content={renderLegend}
              />

              {!isDisabled && <Tooltip content={renderTooltip} />}

              <g>
                <use
                  href={`#${chartId.current}`}
                  style={{
                    pointerEvents: 'none',
                  }}
                />
              </g>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </Spin>
    </div>
  );
}
